import { Injectable } from '@angular/core';
import { PublicClientApplication } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root'
})
export class MsalCleanupService {

  constructor() {}

  /**
   * Limpia completamente el estado de MSAL del navegador
   */
  async clearAllMsalState(): Promise<void> {
    try {
      console.log('🧹 Iniciando limpieza completa de estado MSAL...');

      // 1. Limpiar localStorage
      this.clearLocalStorage();

      // 2. Limpiar sessionStorage
      this.clearSessionStorage();

      // 3. Limpiar cookies
      this.clearMsalCookies();

      // 4. Limpiar IndexedDB (si se usa)
      await this.clearIndexedDB();

      console.log('✅ Estado MSAL limpiado completamente');
    } catch (error) {
      console.error('❌ Error limpiando estado MSAL:', error);
    }
  }

  /**
   * Limpia solo las claves relacionadas con interacciones en progreso
   */
  clearInteractionState(): void {
    try {
      console.log('🔄 Limpiando estado de interacción MSAL...');

      const keysToRemove = [
        'msal.interaction.status',
        'msal.interaction.in.progress',
        'msal.request.state',
        'msal.request.origin',
        'msal.error.interaction.in.progress'
      ];

      // Limpiar localStorage
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Buscar y limpiar claves que contengan "interaction"
      this.clearStorageByPattern('interaction');
      this.clearStorageByPattern('in.progress');
      this.clearStorageByPattern('request.state');

      console.log('✅ Estado de interacción limpiado');
    } catch (error) {
      console.error('❌ Error limpiando estado de interacción:', error);
    }
  }

  /**
   * Verifica si hay estado de interacción persistente
   */
  hasPersistedInteractionState(): boolean {
    const interactionKeys = [
      'msal.interaction.status',
      'msal.interaction.in.progress'
    ];

    for (const key of interactionKeys) {
      if (localStorage.getItem(key) || sessionStorage.getItem(key)) {
        return true;
      }
    }

    // Buscar patrones de interacción
    return this.hasStoragePattern('interaction') || this.hasStoragePattern('in.progress');
  }

  private clearLocalStorage(): void {
    const keys = Object.keys(localStorage);
    const msalKeys = keys.filter(key => 
      key.startsWith('msal.') || 
      key.includes('msal') || 
      key.includes('microsoft') ||
      key.includes('azure')
    );

    msalKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed localStorage: ${key}`);
    });
  }

  private clearSessionStorage(): void {
    const keys = Object.keys(sessionStorage);
    const msalKeys = keys.filter(key => 
      key.startsWith('msal.') || 
      key.includes('msal') || 
      key.includes('microsoft') ||
      key.includes('azure')
    );

    msalKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log(`🗑️ Removed sessionStorage: ${key}`);
    });
  }

  private clearMsalCookies(): void {
    const cookies = document.cookie.split(';');
    
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      if (name.includes('msal') || name.includes('microsoft') || name.includes('azure')) {
        // Clear cookie for current domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        console.log(`🍪 Removed cookie: ${name}`);
      }
    });
  }

  private async clearIndexedDB(): Promise<void> {
    try {
      if ('indexedDB' in window) {
        // Get all databases (if supported)
        if ('databases' in indexedDB) {
          const databases = await (indexedDB as any).databases();
          
          for (const db of databases) {
            if (db.name && (db.name.includes('msal') || db.name.includes('microsoft'))) {
              const deleteRequest = indexedDB.deleteDatabase(db.name);
              await new Promise((resolve, reject) => {
                deleteRequest.onsuccess = () => resolve(true);
                deleteRequest.onerror = () => reject(deleteRequest.error);
              });
              console.log(`🗄️ Removed IndexedDB: ${db.name}`);
            }
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ No se pudo limpiar IndexedDB:', error);
    }
  }

  private clearStorageByPattern(pattern: string): void {
    // localStorage
    const localKeys = Object.keys(localStorage);
    localKeys.filter(key => key.toLowerCase().includes(pattern.toLowerCase()))
             .forEach(key => {
               localStorage.removeItem(key);
               console.log(`🔍 Pattern removed from localStorage: ${key}`);
             });

    // sessionStorage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.filter(key => key.toLowerCase().includes(pattern.toLowerCase()))
               .forEach(key => {
                 sessionStorage.removeItem(key);
                 console.log(`🔍 Pattern removed from sessionStorage: ${key}`);
               });
  }

  private hasStoragePattern(pattern: string): boolean {
    const localKeys = Object.keys(localStorage);
    const sessionKeys = Object.keys(sessionStorage);
    
    return localKeys.some(key => key.toLowerCase().includes(pattern.toLowerCase())) ||
           sessionKeys.some(key => key.toLowerCase().includes(pattern.toLowerCase()));
  }

  /**
   * Reinicia completamente MSAL
   */
  async resetMsalInstance(msalInstance: PublicClientApplication): Promise<void> {
    try {
      console.log('🔄 Reiniciando instancia MSAL...');

      // Limpiar estado
      await this.clearAllMsalState();

      // Reinicializar MSAL
      await msalInstance.initialize();

      console.log('✅ Instancia MSAL reiniciada');
    } catch (error) {
      console.error('❌ Error reiniciando MSAL:', error);
      throw error;
    }
  }

  /**
   * Diagnóstico del estado actual
   */
  diagnosticReport(): any {
    const report = {
      localStorage: this.getStorageReport(localStorage),
      sessionStorage: this.getStorageReport(sessionStorage),
      cookies: this.getCookieReport(),
      hasInteractionState: this.hasPersistedInteractionState(),
      timestamp: new Date().toISOString()
    };

    console.log('📊 MSAL Diagnostic Report:', report);
    return report;
  }

  private getStorageReport(storage: Storage): any {
    const keys = Object.keys(storage);
    const msalKeys = keys.filter(key => 
      key.includes('msal') || 
      key.includes('microsoft') || 
      key.includes('azure') ||
      key.includes('interaction')
    );

    return {
      totalKeys: keys.length,
      msalKeys: msalKeys,
      msalKeyCount: msalKeys.length
    };
  }

  private getCookieReport(): any {
    const cookies = document.cookie.split(';');
    const msalCookies = cookies.filter(cookie => {
      const name = cookie.split('=')[0].trim();
      return name.includes('msal') || name.includes('microsoft') || name.includes('azure');
    });

    return {
      totalCookies: cookies.length,
      msalCookies: msalCookies,
      msalCookieCount: msalCookies.length
    };
  }
}